/**
 * Vanity accessory object builders.
 * Pure computation — no DB calls.
 *
 * Creates mirror, sconce, overhead light, and ceiling light objects
 * for vanity fixture groups.
 */

const {
  deriveWallEndpoints,
  reSnapToWallPreservingLateralPosition,
} = require('./placementUtils');
const { calculateMirrorCenter } = require('./positionMirror');
const {
  computeOverheadLightCenterY,
  clampLateralWithinSpan,
} = require('./accessoryRules');
const { INCHES_TO_METERS } = require('./constants');

/**
 * Resolve a light fixture's dimensions (in meters) from a catalog model,
 * falling back to per-fixture defaults when a field is missing. Catalog
 * models store dimensions in inches; `defaults` must already be in meters.
 *
 * `options.maxWidth` (meters), when given, narrows `x` down to it — never
 * widens. Used by the overhead-wall-light single-sink rule: a light bar
 * wider than the cabinet is the ugliness being fixed, so unlike the
 * mirror this only ever clamps down, it doesn't "match" up.
 */
function resolveLightDimensionsFromModel(modelData, defaults, options = {}) {
  const { maxWidth } = options;
  const d = modelData?.dimensions;
  const x = d?.width * INCHES_TO_METERS || defaults.width;
  return {
    x: maxWidth != null ? Math.min(x, maxWidth) : x,
    y: d?.height * INCHES_TO_METERS || defaults.height,
    z: d?.depth * INCHES_TO_METERS || defaults.depth,
  };
}

/**
 * Calculate mirror center position above a sink, snapped to a wall.
 * Converts wall-axis projection to a lateral point, then delegates to calculateMirrorCenter.
 *
 * @param {Object} options
 * @param {number} options.sinkProj - Sink projection distance along wall (meters from bottomLeft)
 * @param {Object} options.parentWall - Wall object with alignment
 * @param {number} options.countertopTopY - Y of countertop top surface
 * @param {number} options.mirrorHeight - Mirror height in meters
 * @param {number} [options.mirrorDepth] - Mirror depth in meters (back face touches wall when provided)
 * @returns {{ x: number, y: number, z: number }} Mirror center position
 */
function calculateMirrorPositionAboveSink({
  sinkProj,
  parentWall,
  countertopTopY,
  mirrorHeight,
  mirrorDepth,
}) {
  // Endpoints via the shared derivation: a scanned room stores walls as
  // center/dimensions/rotation with no `alignment`, and reading it directly
  // here threw and killed the whole vanity-accessory pass.
  const endpoints = deriveWallEndpoints(parentWall);
  if (!endpoints) {
    throw new Error(
      `calculateMirrorPositionAboveSink: wall ${parentWall?.id} has no usable geometry`
    );
  }
  const wallStart = endpoints.start;
  const wallEnd = endpoints.end;
  const dx = wallEnd.x - wallStart.x;
  const dz = wallEnd.z - wallStart.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  const lateralPos = {
    x: wallStart.x + (dx / len) * sinkProj,
    z: wallStart.z + (dz / len) * sinkProj,
  };

  return calculateMirrorCenter({
    lateralPos,
    countertopTopY,
    mirrorHeight,
    parentWall,
    mirrorDepth,
  });
}

/**
 * Build a wall sconce object at a given lateral position on a wall.
 *
 * @param {Object} options
 * @param {string} options.id
 * @param {Object} options.lateralPoint - {x, z} reference point on wall
 * @param {number} options.centerY
 * @param {Object} options.dimensions - { x, y, z }
 * @param {Object} options.parentWall - wall object (used for snap positioning)
 * @param {Object} options.rotation
 * @param {string} options.fixtureGroupId
 * @param {string} options.parentWallId
 * @param {Object} options.modelData - { id, url, name, scaleFactor }
 * @returns {Object} sconce object
 */
function buildSconceObject({
  id,
  lateralPoint,
  centerY,
  dimensions,
  parentWall,
  rotation,
  fixtureGroupId,
  parentWallId,
  modelData,
  parents,
}) {
  const xz = reSnapToWallPreservingLateralPosition(
    parentWall,
    lateralPoint,
    dimensions.z
  );
  return {
    id,
    type: 'wall-light',
    label: 'Wall Sconce',
    category: { 'wall-light': true },
    fixtureGroupId,
    parentWallIds: [parentWallId],
    ...(parents && { parents }),
    center: { x: xz.x, y: centerY, z: xz.z },
    dimensions,
    rotation: rotation || { x: 0, y: 0, z: 0 },
    // No catalog record → a PROCEDURAL bar. `lightingRenderers` draws an
    // `overhead-wall-light` from its dimensions when it has no model, which
    // is what lets the client-side run engine give a drawn vanity its light
    // without a Firestore query mid-drag; the next cloud touch upgrades it to
    // a product. The fields are OMITTED rather than set to null or
    // undefined: Firestore rejects an undefined value anywhere in a document,
    // and the per-room write is one `update()`.
    ...(modelData
      ? {
          modelId: modelData.id,
          modelUrl: modelData.url,
          modelName: modelData.name,
          ...(modelData.scaleFactor && { scaleFactor: modelData.scaleFactor }),
          ...(modelData.scale && { scale: modelData.scale }),
          ...(modelData.centerOffset && {
            centerOffset: modelData.centerOffset,
          }),
        }
      : { isProcedural: true, modelName: 'Procedural light bar' }),
  };
}

/**
 * Build an overhead wall light object above a mirror.
 *
 * @param {Object} options
 * @param {string} options.id
 * @param {Object} options.mirrorCenter - {x, y, z}
 * @param {number} options.mirrorHeight
 * @param {number} [options.mirrorWidth] - mirror's along-wall width; the span
 *   the light is kept inside when `lateralAnchor` pulls it off centre.
 * @param {Object} options.lightDimensions - { x, y, z }
 * @param {Object} options.rotation
 * @param {string} options.fixtureGroupId
 * @param {string} options.parentWallId
 * @param {Object} options.modelData
 * @param {Object} [options.parentWall] - wall for snap positioning
 * @param {Object} [options.lateralAnchor] - point the light centres on along
 *   the wall (the basin). Omit for the mirror's own centre.
 * @param {Function} [options.projectOntoWall] - required alongside
 *   `lateralAnchor`; from `makeWallProjector`.
 * @returns {Object}
 */
function buildOverheadLightObject({
  id,
  mirrorCenter,
  mirrorHeight,
  mirrorWidth,
  lightDimensions,
  rotation,
  fixtureGroupId,
  parentWallId,
  modelData = null,
  parentWall,
  parents,
  lateralAnchor,
  projectOntoWall,
}) {
  const centerY = computeOverheadLightCenterY(
    mirrorCenter.y,
    mirrorHeight,
    lightDimensions.y
  );

  // An overhead bar is a TASK light: it belongs over the basin, which is not
  // always the mirror's centre (a full-cabinet-width mirror sits on the
  // cabinet while its basin may be slid several inches off — see
  // `clampLateralWithinSpan`). Height still comes from the mirror above.
  // Omitting `lateralAnchor`/`projectOntoWall` reproduces the previous
  // mirror-centred behaviour exactly.
  const anchor = clampLateralWithinSpan({
    target: lateralAnchor,
    spanCenter: mirrorCenter,
    spanWidth: mirrorWidth ?? 0,
    itemWidth: lightDimensions.x,
    projectOntoWall,
  });

  let cx = anchor.x;
  let cz = anchor.z;
  if (parentWall) {
    const xz = reSnapToWallPreservingLateralPosition(
      parentWall,
      anchor,
      lightDimensions.z
    );
    cx = xz.x;
    cz = xz.z;
  }

  return {
    id,
    type: 'overhead-wall-light',
    label: 'Overhead Light',
    category: { 'overhead-wall-light': true },
    fixtureGroupId,
    parentWallIds: [parentWallId],
    ...(parents && { parents }),
    center: { x: cx, y: centerY, z: cz },
    dimensions: lightDimensions,
    rotation: rotation || { x: 0, y: 0, z: 0 },
    // No catalog record → a PROCEDURAL bar. `lightingRenderers` draws an
    // `overhead-wall-light` from its dimensions when it has no model, which
    // is what lets the client-side run engine give a drawn vanity its light
    // without a Firestore query mid-drag; the next cloud touch upgrades it to
    // a product. The fields are OMITTED rather than set to null or
    // undefined: Firestore rejects an undefined value anywhere in a document,
    // and the per-room write is one `update()`.
    ...(modelData
      ? {
          modelId: modelData.id,
          modelUrl: modelData.url,
          modelName: modelData.name,
          ...(modelData.scaleFactor && { scaleFactor: modelData.scaleFactor }),
          ...(modelData.scale && { scale: modelData.scale }),
          ...(modelData.centerOffset && {
            centerOffset: modelData.centerOffset,
          }),
        }
      : { isProcedural: true, modelName: 'Procedural light bar' }),
  };
}

/**
 * Build a ceiling light object above the sink area.
 *
 * @param {Object} options
 * @param {string} options.id
 * @param {Object} options.sinkCenter - { x, z }
 * @param {number} options.ceilingY
 * @param {Object} options.lightDimensions - { x, y, z }
 * @param {string} options.fixtureGroupId
 * @param {Object} options.modelData
 * @returns {Object}
 */
function buildCeilingLightObject({
  id,
  sinkCenter,
  ceilingY,
  lightDimensions,
  fixtureGroupId,
  modelData,
  parents,
  stemLengthInches,
}) {
  return {
    id,
    type: 'ceiling-light',
    label: 'Ceiling Light',
    // Pendants: the renderer shortens the stem band to this; `lightDimensions`
    // must already be the hung height (see chooseVanityCeilingFixture).
    ...(stemLengthInches != null && { stemLengthInches }),
    category: { 'ceiling-light': true },
    fixtureGroupId,
    ...(parents && { parents }),
    center: {
      x: sinkCenter.x,
      y: ceilingY - lightDimensions.y / 2,
      z: sinkCenter.z,
    },
    dimensions: lightDimensions,
    rotation: { x: 0, y: 0, z: 0 },
    modelId: modelData.id,
    modelUrl: modelData.url,
    modelName: modelData.name,
  };
}

/**
 * XZ center for a ceiling light above a vanity — averages the fixture
 * group's sink positions; falls back to `fallbackCenter` (usually the
 * vanity or cabinet center) when no sinks are found.
 */
function computeVanityCeilingLightXZ({
  updatedObjects,
  fixtureGroupId,
  fallbackCenter,
}) {
  const sinks = updatedObjects.filter(
    (o) =>
      o.fixtureGroupId === fixtureGroupId &&
      (o.type || '').toLowerCase().includes('sink') &&
      !(o.type || '').toLowerCase().includes('faucet')
  );
  if (sinks.length === 0) return { x: fallbackCenter.x, z: fallbackCenter.z };
  return {
    x: sinks.reduce((sum, s) => sum + s.center.x, 0) / sinks.length,
    z: sinks.reduce((sum, s) => sum + s.center.z, 0) / sinks.length,
  };
}

/**
 * Reposition an existing overhead light to follow a (possibly resized
 * or moved) mirror. Centers on the mirror's lateral wall projection,
 * re-snaps depth at the overhead's own depth, and recomputes Y via
 * `computeOverheadLightCenterY` from the mirror.
 *
 * Pure — returns the new center; caller decides collision + apply.
 */
function repositionOverheadAboveMirror({
  overhead,
  mirror,
  parentWall,
  lateralAnchor,
  projectOntoWall,
}) {
  // Same rule as `buildOverheadLightObject`: lateral position follows the
  // BASIN when one is supplied (clamped inside the mirror), height follows
  // the mirror. Omitting the two optional args keeps the old behaviour.
  const anchor = clampLateralWithinSpan({
    target: lateralAnchor,
    spanCenter: mirror?.center ?? overhead.center,
    spanWidth: mirror?.dimensions?.x ?? 0,
    itemWidth: overhead.dimensions?.x ?? 0,
    projectOntoWall,
  });
  const xz = reSnapToWallPreservingLateralPosition(
    parentWall,
    anchor,
    overhead.dimensions?.z || 0.1
  );
  const hasMirror = mirror?.center?.y != null && mirror?.dimensions?.y != null;
  const newY = hasMirror
    ? computeOverheadLightCenterY(
        mirror.center.y,
        mirror.dimensions.y,
        overhead.dimensions?.y || 0.15
      )
    : overhead.center.y;
  return { x: xz.x, y: newY, z: xz.z };
}

module.exports = {
  calculateMirrorPositionAboveSink,
  buildSconceObject,
  buildOverheadLightObject,
  buildCeilingLightObject,
  computeVanityCeilingLightXZ,
  resolveLightDimensionsFromModel,
  repositionOverheadAboveMirror,
};
